import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ApplicationSdk } from "..";

// sort folders and trainings by their position
const sortByPosAndCompleted = (a: any, b: any) => {
    if (a.pos === b.pos) {
        return 0;
    }
    return a.pos < b.pos ? -1 : 1;
};

// Todo: was ist für den Fall das keine Daten geladen werden können?
class TrainingStore {
    private sdk: ApplicationSdk;

    public trainingListLoading: boolean = false;
    public folderList: any = [];
    public trainingList: any = [];

    public defaultFolder: number | null = null;
    public currentFolder: number | null = null;
    public currentTrainingId: number | null = null;
    public currentTrainingSession: any = null;
    public currentAnswer: any = [];

    public s3urlFolder: string = '';
    public s3urlTraining: string = '';

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            trainingListLoading: observable,
            trainingList: observable,
            folderList: observable,
            s3urlFolder: observable,
            s3urlTraining: observable,
            currentFolder: observable,
            currentTrainingId: observable,
            currentTrainingSession: observable,
            currentAnswer: observable,
            setCurrentFolder: action,
            quitTraining: action,
            setCurrentTraining: action,
            trainingFinishFailed: action,
            selectAnswer: action,
            treeData: computed,
            currentTree: computed,
            currentTraining: computed,
        });
        this.sdk = sdk;
    }

    public async init(): Promise<void> {
        this.fetchTrainingList();
    }

    public async fetchTrainingList(): Promise<void> {
        runInAction(() => {
            this.trainingListLoading = true;
        });
        const trainingListResult: any = await this.sdk.fetchApi(
            'training',
            'list',
            {
            });

        runInAction(() => {
            this.trainingListLoading = false;

            if (trainingListResult?.status === 200) {
                this.folderList = trainingListResult.json.data.folders || [];
                this.trainingList = trainingListResult.json.data.trainings || [];

                this.folderList.find((folder: any) => {
                    if (folder.parent_id === null) {
                        runInAction(() => {
                            this.currentFolder = folder.id;
                            this.defaultFolder = folder.id;
                        });
                        return true;
                    }
                    return false;
                });

                this.s3urlFolder = trainingListResult.json.s3url.folder || '';
                this.s3urlTraining = trainingListResult.json.s3url.training || '';
            }
        });
    }

    public async startTraining(): Promise<void> {
        const trainingStartResult: any = await this.sdk.fetchApi(
            'training',
            'start',
            {
                training_id: this.currentTrainingId,
            });

        if (trainingStartResult?.status === 200) {
            runInAction(() => {
                this.currentTrainingSession = trainingStartResult.json.session || null;
            })
        }
    }

    public async answerQuestion(answers: Array<number>): Promise<void> {
        const trainingStartResult: any = await this.sdk.fetchApi(
            'training',
            'answer',
            {
                session_id: this.currentTrainingSession.id,
                question_id: this.currentTrainingSession.question.question_id,
                answers,
            });

        if (trainingStartResult?.status === 200) {
            const { correct, question } = trainingStartResult.json;
            runInAction(() => {
                this.currentTrainingSession = {
                    ...this.currentTrainingSession,
                    question: {
                        ...this.currentTrainingSession.question,
                        ...question,
                        correct: correct,
                        finished: correct === true || correct === false,
                    }
                }
            })
        }
    }

    public async nextQuestion(): Promise<void> {
        const trainingNextResult: any = await this.sdk.fetchApi(
            'training',
            'next',
            {
                session_id: this.currentTrainingSession.id
            });

        if (trainingNextResult?.status === 200) {
            const { session } = trainingNextResult.json;

            runInAction(() => {
                this.currentAnswer = [];
                this.currentTrainingSession = {
                    ...this.currentTrainingSession,
                    ...session
                }
            })
        }
    }

    public trainingFinishFailed(): void {
        runInAction(() => {
            this.currentTrainingSession = {
                ...this.currentTrainingSession,
                question: {
                    ...this.currentTrainingSession.question
                },
                finished: true,
                failed: true
            };
        })
    }

    public quitTraining(): void {
        this.currentTrainingId = null;
        this.currentFolder = this.defaultFolder;
        this.currentTrainingSession = null;
        this.currentAnswer = [];

        this.fetchTrainingList();
    }

    public selectAnswer(answerId: number): void {
        if (this.currentAnswer.includes(answerId)) {
            this.currentAnswer = this.currentAnswer.filter((id: number) => id !== answerId);
        }
        else {
            this.currentAnswer.push(answerId);
        }
    }

    public setCurrentFolder(folderId: number | null): void {
        if (this.currentFolder !== folderId) {
            this.currentFolder = folderId;
        }
    }

    public setCurrentTraining(trainingId: number | null): void {
        this.currentTrainingId = trainingId;
    }

    get currentTraining(): any {
        if (this.currentTrainingId === null) {
            return null; // No current training if no training is selected
        }

        const training = this.trainingList.find((training: any) => training.id === this.currentTrainingId);
        if (training) {
            return {
                id: training.id,
                label: training.name,
                data: training.description,
                video: training.video_url,
                completed: training.completed,
                img: training.image ?
                    this.s3urlTraining + training.image + '.jpeg' :
                    null,
                session: this.currentTrainingSession
            };
        }
        return null;
    }

    get previousFolder(): any {
        if (this.currentFolder === null) {
            return null; // No previous folder if no current folder is selected
        }

        const findParentFolder = (nodes: any[], folderId: number): any | null => {
            for (const node of nodes) {
                if (node.children && node.children.some((child: any) => child.id === folderId)) {
                    return node; // Return the parent folder
                }
                if (node.children && node.children.length > 0) {
                    const result = findParentFolder(node.children, folderId);
                    if (result) {
                        return result;
                    }
                }
            }
            return null;
        };

        const parentFolder = findParentFolder(this.treeData, this.currentFolder);

        if (parentFolder) {
            return {
                id: parentFolder.id,
                key: `folder-${parentFolder.id}`,
                label: '...',
                type: "folder",
                data: 'Gehe einen Ordner zurück',
                backbutton: true,
                children: parentFolder.children
            };
        }
        return null;
    }

    get currentTree(): any {
        if (this.currentFolder === null) {
            return this.treeData; // Return the entire tree if no currentFolder is set
        }

        const findSubtree = (nodes: any[], folderId: number): any[] => {
            for (const node of nodes) {
                if (node.type === 'folder') {
                    if (node.id === folderId) {
                        return node.children; // Return the subtree starting at the current folder
                    }
                    if (node.children && node.children.length > 0) {
                        const result = findSubtree(node.children, folderId);
                        if (result?.length > 0) {
                            return result;
                        }
                    }
                }
            }
            return [];
        };

        return findSubtree(this.treeData, this.currentFolder);
    }


    public async backToChat(): Promise<void> {
        const phonenumber = await this.sdk.fetchApi(
            'cleaner',
            'backtochat',
            {});

        if (phonenumber?.status === 200) {
            const { redirect_url } = phonenumber.json;
            if (redirect_url) {
                window.open(redirect_url, '_blank');
            }
        }
    }

    get treeData(): any[] {
        const folderMap = new Map();

        // Erst alle Ordner vorbereiten
        this.folderList.forEach((folder: any) => {
            folderMap.set(folder.id, {
                id: folder.id,
                key: `folder-${folder.id}`,
                label: folder.name,
                pos: folder.pos,
                type: "folder",
                data: folder.description,
                img: folder.image ?
                    this.s3urlFolder + folder.image + '.jpeg' :
                    null,
                children: []
            });
        });

        // Trainings in Ordner einsortieren
        this.trainingList.forEach((training: any) => {
            const parent = folderMap.get(training.folder_id);
            if (parent) {
                parent.children.push({
                    id: training.id,
                    key: `training-${training.id}`,
                    label: training.name,
                    completed: training.completed,
                    pos: training.completed ? 10000 + training.pos : training.pos,
                    type: "training",
                    bgcolor: training.completed ? '#e8f5e9' : undefined,
                    data: training.description,
                    img: training.image ?
                        this.s3urlTraining + training.image + '.jpeg' :
                        null
                });
            }
        });

        // Hierarchie aufbauen
        const tree: any = [];
        this.folderList.forEach((folder: any) => {
            const node = folderMap.get(folder.id);
            if (folder.parent_id) {
                if (folderMap.has(folder.parent_id)) {
                    folderMap.get(folder.parent_id).children.push(node);
                }
            } else {
                tree.push({
                    ...node
                });
            }
        });

        // Sort the whole tree and its children by position
        const sortTree = (nodes: any[]): any[] => {
            return nodes
                .map((node) => {
                    if (node.children) {
                        node.children = sortTree(node.children);
                    }
                    return node;
                })
                .sort(sortByPosAndCompleted);
        };

        return sortTree(tree);
    }

}

export default TrainingStore;